import defaultContent from '../../../content/userList';
import { useTranslation } from 'react-i18next';
import Title from '../../common/Title/Title';
import Button from '../../common/Button/Button';
import Table from '../../common/Table/Table';
import { useDeleteUserMutation, useGetUsersQuery } from '../../../redux/services/usersAPI';
import { GroupRecord, UserRecord } from '../../../redux/services/types/types';
import { ColumnDef } from '../../common/Table/useTable';
import IconButton from '../../common/IconButton/IconButton';
import styles from './UserList.module.scss';
import StatusPill from '../../common/StatusPill/StatusPill';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import Confirm from '../../common/Confirm/Confirm';
import { USER_STATUS } from '../../../constants/constants';
import { useToast } from '../../common/Toast/ToastContext';
import { useGetGroupsQuery } from '../../../redux/services/groupsAPI';

export const UserList = () => {
  const { t } = useTranslation(['userList']);
  const title = t('userList', defaultContent['userList']).toString();

  const navigate = useNavigate();

  const { data: groups } = useGetGroupsQuery();

  const { data, isLoading } = GetAndTransformUserList(groups ?? []);

  const colDefs: Array<ColumnDef<UserRecord>> = [
    { field: 'fullName', header: 'Full Name' },
    { field: 'nativeId', header: 'Native Id' },
    { field: 'affiliateCode', header: 'Affiliate Code' },
    { field: 'permissionGroupsName', header: 'Group' },
    {
      field: 'status',
      header: 'Status',
      renderCell: (record) => {
        switch (record.status) {
          case USER_STATUS.ACTIVE:
            return <StatusPill color="primary-purple" text="Active" size="small" />;
          case USER_STATUS.SUSPENDED:
            return <StatusPill color="primary" text="Suspended" size="small" />;
          case USER_STATUS.INACTIVE:
            return <StatusPill color="primary" text="Inactive" size="small" />;
          default:
            return '';
        }
      },
    },
    {
      header: 'Actions',
      renderCell: (record, colDef) => (
        <div className={styles['user-actions']}>
          <IconButton
            disabled={false}
            icon="edit"
            action={() => navigate(`detail?nativeId=${record.nativeId}`)}
            data-testid={`edit-button-${record.nativeId}`}
          />
          <DeleteUserButton id={record.userId} />
        </div>
      ),
    },
  ];

  return (
    <div className="container">
      <Title text={title} />
      <div className="row">
        <div className="column-one right">
          <Button
            text="Add User"
            color="primary-black"
            disabled={false}
            action={function (): void {
              navigate('detail');
            }}
          />
        </div>
      </div>
      <div className="h-2"></div>
      <Table
        colDefs={colDefs}
        data={data || []}
        isLoading={isLoading}
        getRowId={(user) => user.userId.toString()}
      />
    </div>
  );
};

const GetAndTransformUserList = (permissionGroups: GroupRecord[]) => {
  const { data, isLoading } = useGetUsersQuery();

  let userData: UserRecord[] = [];

  if (data && data.length > 0) {
    userData = data.map((user) => {
      const userPermissionGroups = user.userPermissionGroups
        ? user.userPermissionGroups.map((userPermission) => {
            return permissionGroups.find(
              (x) => x.permissionGroupId === userPermission.permissionGroupId,
            )?.name;
          })
        : [];

      const newUser = { ...user, permissionGroupsName: userPermissionGroups.join(' | ') };

      return newUser;
    });

    return { data: userData, isLoading };
  }

  return { data, isLoading };
};

export const DeleteUserButton = ({ id }: { id: number }) => {
  const [deleteUser, { isLoading }] = useDeleteUserMutation();
  const { refetch } = useGetUsersQuery();

  const [confirm, setConfirm] = useState(false);

  const { addToast } = useToast();

  const onDelete = () =>
    deleteUser(id).then((res) => {
      if ('error' in res) {
        addToast({
          content: 'Failed to delete user !',
          type: 'error',
        });
      } else {
        refetch();
        setConfirm(false);
        addToast({
          content: 'User deleted successfully !',
          type: 'success',
        });
      }
    });

  return (
    <>
      <IconButton
        disabled={isLoading}
        icon="delete"
        action={() => setConfirm(true)}
        data-testid="delete-icon"
      />
      <Confirm
        open={confirm}
        title="Delete User?"
        description="Are you sure you want to permanently delete this user? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        onConfirm={onDelete}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
};

export default UserList;
